<template>
  <div class="tb-sync-log-panel">
    <el-table
      :data="historyList"
      border
      size="medium"
      height="100%"
      v-loading="historyLoading"
      class="custom-table"
      style="width:100%"
      empty-text="暂无数据"
    >
      <el-table-column prop="id" :label="ui.recordId" width="130">
        <template slot-scope="scope">
          <span class="detail-id-cell">{{ scope.row.id }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="config.deptColumnLabel" min-width="140" show-overflow-tooltip>
        <template slot-scope="scope">{{ scope.row.rootDepartmentName || '' }}</template>
      </el-table-column>
      <el-table-column :label="ui.status" width="90">
        <template slot-scope="scope">
          <el-tag :type="getStatusMeta(scope.row.status).type" size="mini">
            {{ getStatusMeta(scope.row.status).text }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="ui.success" width="64" align="center">
        <template slot-scope="scope">
          <span
            v-if="getLogCount(scope.row, 'successCount') > 0"
            class="count-link count-link-ok"
            :title="ui.viewCountDetail"
            @click.stop="openDetailDialog(scope.row.id, 1)"
          >{{ scope.row.successCount }}</span>
          <span v-else class="count-muted">0</span>
        </template>
      </el-table-column>
      <el-table-column :label="ui.fail" width="64" align="center">
        <template slot-scope="scope">
          <span
            v-if="getLogCount(scope.row, 'failCount') > 0"
            class="count-link count-link-fail"
            :title="ui.viewCountDetail"
            @click.stop="openDetailDialog(scope.row.id, 2)"
          >{{ scope.row.failCount }}</span>
          <span v-else class="count-muted">0</span>
        </template>
      </el-table-column>
      <el-table-column :label="ui.skip" width="64" align="center">
        <template slot-scope="scope">
          <span
            v-if="getLogCount(scope.row, 'skipCount') > 0"
            class="count-link count-link-skip"
            :title="ui.viewCountDetail"
            @click.stop="openDetailDialog(scope.row.id, 3)"
          >{{ scope.row.skipCount }}</span>
          <span v-else class="count-muted">0</span>
        </template>
      </el-table-column>
      <el-table-column prop="startTime" :label="ui.syncTime" width="168" show-overflow-tooltip></el-table-column>
      <el-table-column prop="triggerUserName" :label="ui.triggerUser" min-width="88" show-overflow-tooltip></el-table-column>
      <el-table-column :label="ui.action" width="148" align="center" class-name="tb-sync-op-col">
        <template slot-scope="scope">
          <div class="tb-sync-op-cell">
            <el-button type="text" size="small" class="link-btn" @click="openDetailDialog(scope.row.id, 0)">{{ ui.viewDetail }}</el-button>
            <el-button type="text" size="small" class="link-btn link-btn-danger" @click="deleteRecord(scope.row)">{{ ui.delete }}</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination-bar">
      <el-pagination
        small
        layout="total, sizes, prev, pager, next, jumper"
        :total="historyTotal"
        :page-size="historyPageSize"
        :page-sizes="[10, 20, 50]"
        :current-page.sync="historyPage"
        @current-change="fetchHistoryPage"
        @size-change="onHistoryPageSizeChange"
      />
    </div>

    <el-dialog
      :title="detailDialogTitle"
      :visible.sync="detailDialogVisible"
      width="960px"
      top="6vh"
      append-to-body
      custom-class="tb-sync-detail-dialog"
      @closed="onDetailDialogClosed"
      @opened="layoutDetailTable"
    >
      <div v-if="!logSummary" class="dialog-loading">
        <i class="el-icon-loading"></i>
        <span>{{ ui.loading }}</span>
      </div>
      <template v-else>
        <div class="detail-summary-bar">
          <div class="summary-status">
            <el-tag :type="getStatusMeta(logSummary.status).type" size="medium">
              {{ getStatusMeta(logSummary.status).text }}
            </el-tag>
            <div class="summary-status-text">
              <span class="summary-dept">{{ logSummary.rootDepartmentName || '' }}</span>
              <div class="detail-meta-row">
                <span><i class="el-icon-user"></i> {{ ui.triggerUserLabel }}{{ logSummary.triggerUserName || '' }}</span>
                <span><i class="el-icon-time"></i> {{ ui.syncTimeLabel }}{{ logSummary.startTime || '' }}</span>
                <span v-if="config.skipHint" class="skip-hint">{{ config.skipHint }}</span>
              </div>
            </div>
          </div>
          <div class="summary-stats">
            <div
              class="mini-stat stat-ok"
              :class="{ 'mini-stat-clickable': getLogCount(logSummary, 'successCount') > 0 }"
              @click="switchDetailFilter(1)"
            >
              <span class="mini-stat-label">{{ ui.success }}</span>
              <span class="mini-stat-value">{{ logSummary.successCount }}</span>
            </div>
            <div
              class="mini-stat stat-fail"
              :class="{ 'mini-stat-clickable': getLogCount(logSummary, 'failCount') > 0 }"
              @click="switchDetailFilter(2)"
            >
              <span class="mini-stat-label">{{ ui.fail }}</span>
              <span class="mini-stat-value">{{ logSummary.failCount }}</span>
            </div>
            <div
              class="mini-stat stat-skip"
              :class="{ 'mini-stat-clickable': getLogCount(logSummary, 'skipCount') > 0 }"
              @click="switchDetailFilter(3)"
            >
              <span class="mini-stat-label">{{ ui.skip }}</span>
              <span class="mini-stat-value">{{ logSummary.skipCount }}</span>
            </div>
          </div>
        </div>

        <div v-if="logSummary.errorMessage" class="detail-error-msg">
          <i class="el-icon-warning-outline"></i>
          {{ logSummary.errorMessage }}
        </div>

        <div class="detail-toolbar">
          <div class="detail-toolbar-left">
            <el-radio-group v-model="resultFilter" size="small" @change="onFilterChange">
              <el-radio-button :label="0">{{ ui.all }}</el-radio-button>
              <el-radio-button :label="1">{{ ui.onlySuccess }}</el-radio-button>
              <el-radio-button :label="2">{{ ui.onlyFail }}</el-radio-button>
              <el-radio-button :label="3">{{ ui.onlySkip }}</el-radio-button>
            </el-radio-group>
            <el-input
              v-model="detailKeyword"
              size="small"
              clearable
              :placeholder="detailSearchPlaceholder"
              prefix-icon="el-icon-search"
              class="detail-search-input"
              @keyup.enter.native="onDetailSearch"
              @clear="onDetailSearch"
            />
            <el-button size="small" type="primary" plain icon="el-icon-search" @click="onDetailSearch">{{ ui.search }}</el-button>
          </div>
          <el-button size="small" icon="el-icon-refresh" :loading="detailLoading" @click="refreshMonitor">{{ ui.refresh }}</el-button>
        </div>

        <div v-if="showSkipDetailLegacyHint" class="detail-skip-legacy-hint">
          <i class="el-icon-info"></i>
          <span>{{ skipDetailLegacyHintText }}</span>
        </div>

        <el-table
          ref="detailTable"
          :data="detailList"
          border
          size="small"
          max-height="420"
          v-loading="detailLoading"
          class="custom-table dialog-table"
        >
          <el-table-column
            v-for="col in config.detailColumns"
            :key="col.prop"
            :prop="col.prop"
            :label="col.label"
            :width="col.width"
            :min-width="col.minWidth"
            :align="col.align"
            :header-align="col.headerAlign || col.align"
            :show-overflow-tooltip="col.columnType === 'remark' || (!col.columnType && !isAxdSyncEntityIdProp(col.prop) && !isAxdSyncVendorIdProp(col.prop))"
          >
            <template slot-scope="scope">
              <template v-if="col.columnType === 'syncResult'">
                <el-tag v-if="isSkipDetailRow(scope.row)" type="warning" size="mini">{{ ui.skip }}</el-tag>
                <el-tag v-else :type="scope.row.isSuccess ? 'success' : 'danger'" size="mini">
                  {{ scope.row.isSuccess ? ui.success : ui.fail }}
                </el-tag>
              </template>
              <span v-else-if="col.columnType === 'remark'" class="detail-remark-cell">
                {{ formatAxdSyncRemarkDisplay(scope.row) }}
              </span>
              <span
                v-else-if="isAxdSyncVendorIdProp(col.prop) && formatAxdSyncVendorIdDisplay(scope.row[col.prop])"
                class="detail-id-cell detail-id-cell-ellipsis"
              >
                {{ formatAxdSyncVendorIdDisplay(scope.row[col.prop]) }}
              </span>
              <span
                v-else-if="isAxdSyncEntityIdProp(col.prop) && formatAxdSyncCellDisplay(scope.row[col.prop])"
                class="detail-id-cell detail-id-cell-ellipsis"
              >
                {{ formatAxdSyncCellDisplay(scope.row[col.prop]) }}
              </span>
              <span v-else-if="!isAxdSyncVendorIdProp(col.prop) && !isAxdSyncEntityIdProp(col.prop)">
                {{ formatAxdSyncCellDisplay(scope.row[col.prop]) }}
              </span>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-bar detail-body-paging">
          <el-pagination
            v-if="logSummary"
            small
            layout="total, sizes, prev, pager, next, jumper"
            :total="detailTotal"
            :page-size="detailPageSize"
            :page-sizes="[10, 20, 50]"
            :current-page.sync="detailPage"
            @size-change="onDetailPageSizeChange"
            @current-change="fetchDetailPage"
          />
        </div>
      </template>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="detailDialogVisible = false">{{ ui.close }}</el-button>
        <el-button v-if="logSummary" size="small" type="danger" plain icon="el-icon-delete" @click="deleteCurrentRecord">{{ ui.deleteCurrent }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  axdSyncApiMap,
  normalizeAxdSyncLogPayload,
  isAxdSyncEntityIdProp,
  isAxdSyncVendorIdProp,
  formatAxdSyncVendorIdDisplay,
  formatAxdSyncCellDisplay,
  formatAxdSyncRemarkDisplay,
  AXD_SYNC_LOG_PANEL_UI,
} from './axdSyncApiMap';

/**
 * 奥讯达同步记录面板（历史列表 + 明细弹窗）
 * 创建于 2026-06-13
 */
export default {
  name: 'AxdSyncLogPanel',
  props: {
    syncType: {
      type: String,
      required: true,
      validator: (v) => ['dept', 'student', 'parent'].includes(v),
    },
    rootDepartmentId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      historyList: [],
      historyTotal: 0,
      historyPage: 1,
      historyPageSize: 10,
      historyLoading: false,
      detailDialogVisible: false,
      logId: null,
      logSummary: null,
      detailList: [],
      detailTotal: 0,
      detailPage: 1,
      detailPageSize: 10,
      detailLoading: false,
      resultFilter: 0,
      detailKeyword: '',
      pollTimer: null,
    };
  },
  computed: {
    config() {
      return axdSyncApiMap[this.syncType] || axdSyncApiMap.student;
    },
    ui() {
      return AXD_SYNC_LOG_PANEL_UI;
    },
    detailDialogTitle() {
      if (!this.logId) return AXD_SYNC_LOG_PANEL_UI.detailDialogTitle;
      return `${AXD_SYNC_LOG_PANEL_UI.detailDialogTitle} #${this.logId}`;
    },
    detailSearchPlaceholder() {
      return (this.config && this.config.detailSearchPlaceholder)
        || AXD_SYNC_LOG_PANEL_UI.searchPlaceholderFallback;
    },
    showSkipDetailLegacyHint() {
      if (this.resultFilter !== 3 || !this.logSummary || this.detailLoading) return false;
      if (this.detailTotal > 0) return false;
      return this.getLogCount(this.logSummary, 'skipCount') > 0;
    },
    skipDetailLegacyHintText() {
      const count = this.getLogCount(this.logSummary, 'skipCount');
      return AXD_SYNC_LOG_PANEL_UI.skipDetailLegacyHint.replace('{count}', count);
    },
  },
  watch: {
    rootDepartmentId() {
      this.resetAndLoad();
    },
    syncType() {
      this.resetAndLoad();
    },
  },
  destroyed() {
    this.stopPoll();
  },
  methods: {
    getStatusMeta(status) {
      const s = Number(status);
      const ui = AXD_SYNC_LOG_PANEL_UI;
      if (s === 0) return { type: 'warning', text: ui.statusRunning };
      if (s === 1) return { type: 'success', text: ui.statusDone };
      return { type: 'danger', text: ui.statusFailed };
    },
    /** 读取日志计数字段，兼容 camelCase / PascalCase */
    getLogCount(row, field) {
      if (!row) return 0;
      const pascal = field.charAt(0).toUpperCase() + field.slice(1);
      const val = row[field] != null ? row[field] : row[pascal];
      const num = Number(val);
      return Number.isFinite(num) ? num : 0;
    },
    resetAndLoad() {
      this.stopPoll();
      this.closeDetailDialog(false);
      this.historyPage = 1;
      if (this.rootDepartmentId) {
        this.fetchHistoryPage();
      } else {
        this.historyList = [];
        this.historyTotal = 0;
      }
    },
    getLatestLog() {
      return this.historyList && this.historyList.length > 0 ? this.historyList[0] : null;
    },
    reloadHistory() {
      this.fetchHistoryPage();
    },
    /** 切换每页条数后回到第一页重新拉取。最后更新：2026-08-31 */
    onHistoryPageSizeChange(size) {
      this.historyPageSize = size;
      this.historyPage = 1;
      this.fetchHistoryPage();
    },
    /** 明细弹窗切换每页条数。最后更新：2026-08-31 */
    onDetailPageSizeChange(size) {
      this.detailPageSize = size;
      this.detailPage = 1;
      this.fetchDetailPage();
    },
    fetchHistoryPage() {
      if (!this.rootDepartmentId) return Promise.resolve();
      this.historyLoading = true;
      return this.config.getLogPage({
        rootDepartmentId: this.rootDepartmentId,
        pageIndex: this.historyPage,
        pageSize: this.historyPageSize,
      }).then((res) => {
        this.historyLoading = false;
        if (res.data.success && res.data.response) {
          const p = res.data.response;
          const rows = p.data != null ? p.data : p.Data;
          this.historyTotal = p.dataCount != null ? p.dataCount : p.DataCount;
          this.historyList = (rows || []).map((r) => normalizeAxdSyncLogPayload(r)).filter(Boolean);
          this.$emit('history-loaded', this.getLatestLog());
        }
      }).catch(() => {
        this.historyLoading = false;
      });
    },
    openDetailDialog(syncLogId, resultFilter = 0) {
      this.logId = syncLogId;
      this.detailPage = 1;
      this.resultFilter = resultFilter || 0;
      this.detailKeyword = '';
      this.logSummary = null;
      this.detailList = [];
      this.detailDialogVisible = true;
      this.stopPoll();
      this.refreshMonitor();
      this.fetchLogOnly().then(() => {
        if (this.logSummary && Number(this.logSummary.status) === 0) {
          this.startPoll();
        }
      });
    },
    switchDetailFilter(filter) {
      if (!this.logSummary) return;
      const countMap = {
        1: this.getLogCount(this.logSummary, 'successCount'),
        2: this.getLogCount(this.logSummary, 'failCount'),
        3: this.getLogCount(this.logSummary, 'skipCount'),
      };
      if (!(countMap[filter] > 0)) return;
      if (this.resultFilter === filter) {
        this.fetchDetailPage();
        return;
      }
      this.resultFilter = filter;
      this.detailPage = 1;
      this.fetchDetailPage();
    },
    isSkipDetailRow(row) {
      return row && String(row.operation || '') === AXD_SYNC_LOG_PANEL_UI.skip;
    },
    isAxdSyncEntityIdProp,
    isAxdSyncVendorIdProp,
    formatAxdSyncVendorIdDisplay,
    formatAxdSyncCellDisplay,
    formatAxdSyncRemarkDisplay,
    closeDetailDialog(refreshHistory) {
      this.stopPoll();
      this.detailDialogVisible = false;
      this.logId = null;
      this.logSummary = null;
      this.detailList = [];
      this.detailTotal = 0;
      if (refreshHistory !== false) {
        this.fetchHistoryPage();
      }
    },
    onDetailDialogClosed() {
      this.stopPoll();
      this.logId = null;
      this.logSummary = null;
      this.detailList = [];
      this.detailTotal = 0;
      this.detailKeyword = '';
    },
    onFilterChange() {
      this.detailPage = 1;
      this.fetchDetailPage();
    },
    onDetailSearch() {
      this.detailPage = 1;
      this.fetchDetailPage();
    },
    fetchLogOnly() {
      if (!this.logId) return Promise.resolve();
      return this.config.getLog({ syncLogId: this.logId }).then((res) => {
        if (res.data.success && res.data.response) {
          this.logSummary = normalizeAxdSyncLogPayload(res.data.response);
        }
      });
    },
    refreshMonitor() {
      this.fetchLogOnly();
      this.fetchDetailPage();
    },
    fetchDetailPage() {
      if (!this.logId) return;
      this.detailLoading = true;
      const keyword = (this.detailKeyword || '').trim();
      return this.config.getDetailPage({
        syncLogId: this.logId,
        pageIndex: this.detailPage,
        pageSize: this.detailPageSize,
        resultFilter: this.resultFilter,
        keyword: keyword || undefined,
      }).then((res) => {
        this.detailLoading = false;
        if (res.data.success && res.data.response) {
          const p = res.data.response;
          const rows = p.data != null ? p.data : p.Data;
          this.detailTotal = p.dataCount != null ? p.dataCount : p.DataCount;
          this.detailList = (rows || []).map((r) => this.config.normalizeDetail(r));
          this.layoutDetailTable();
        }
      }).catch(() => {
        this.detailLoading = false;
      });
    },
    layoutDetailTable() {
      const runLayout = () => {
        const table = this.$refs.detailTable;
        if (table && typeof table.doLayout === 'function') {
          table.doLayout();
        }
      };
      this.$nextTick(runLayout);
      setTimeout(runLayout, 320);
    },
    startPoll() {
      this.stopPoll();
      this.pollTimer = setInterval(() => {
        if (!this.logId || !this.detailDialogVisible) {
          this.stopPoll();
          return;
        }
        this.fetchLogOnly().then(() => {
          const s = this.logSummary;
          if (s != null && Number(s.status) !== 0) {
            this.stopPoll();
            this.fetchDetailPage();
            this.fetchHistoryPage();
            this.$emit('sync-completed');
          }
        });
      }, 2500);
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    deleteRecord(row) {
      if (!row || row.id == null) return;
      const ui = AXD_SYNC_LOG_PANEL_UI;
      const nameHint = row.rootDepartmentName ? `【${row.rootDepartmentName}】` : '';
      const message = ui.deleteConfirmMessage
        .replace('{nameHint}', nameHint)
        .replace('{id}', row.id);
      this.$confirm(message, ui.deleteConfirmTitle, { type: 'warning' })
        .then(() => {
          this.config.deleteLog({ syncLogId: row.id }).then((res) => {
            if (res.data.success) {
              this.$message.success(res.data.msg || ui.deletedMsg);
              if (this.logId === row.id) {
                this.closeDetailDialog(true);
              } else {
                this.fetchHistoryPage();
              }
              this.$emit('record-deleted');
            } else {
              this.$message.error(res.data.msg || ui.deleteFailedMsg);
            }
          });
        })
        .catch(() => {});
    },
    deleteCurrentRecord() {
      if (!this.logSummary) return;
      this.deleteRecord(this.logSummary);
    },
  },
};
</script>

<style scoped>
.tb-sync-log-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 0;
}

.tb-sync-log-panel > .custom-table {
  flex: 1;
  min-height: 0;
}

/* 表体撑满记录卡片。height=100% 在 flex 里常被算成内容高度。最后修改：2026-10-14 */
@media (min-width: 769px) {
  .tb-sync-log-panel > .custom-table {
    height: 0 !important;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .tb-sync-log-panel ::v-deep .el-table__header-wrapper {
    flex-shrink: 0;
  }
  .tb-sync-log-panel ::v-deep .el-table__body-wrapper {
    flex: 1 1 auto !important;
    min-height: 0;
    height: auto !important;
    max-height: none !important;
    overflow: auto !important;
  }
}

.pagination-bar {
  flex-shrink: 0;
  margin-top: 12px;
  text-align: right;
  overflow: hidden;
}

.link-btn {
  color: #4f46e5 !important;
  font-weight: 500;
  margin: 0 !important;
  padding: 2px 6px !important;
}

.link-btn-danger {
  color: #ef4444 !important;
}

.tb-sync-op-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 2px;
}

.count-link {
  cursor: pointer !important;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.15s ease, background-color 0.15s ease;
  display: inline-block;
  min-width: 20px;
  padding: 2px 6px;
  border-radius: 4px;
  user-select: none;
}

.count-link-ok {
  color: #059669 !important;
}

.count-link-ok:hover {
  color: #047857 !important;
  background: rgba(5, 150, 105, 0.1);
}

.count-link-fail {
  color: #dc2626 !important;
}

.count-link-fail:hover {
  color: #b91c1c !important;
  background: rgba(220, 38, 38, 0.08);
}

.count-link-skip {
  color: #d97706 !important;
}

.count-link-skip:hover {
  color: #b45309 !important;
  background: rgba(217, 119, 6, 0.1);
}

.count-muted {
  color: #cbd5e1;
  cursor: default;
}

.tb-sync-log-panel ::v-deep .el-table td .count-link {
  cursor: pointer !important;
}

.tb-sync-log-panel ::v-deep .el-table td .count-link-ok {
  color: #059669 !important;
}

.tb-sync-log-panel ::v-deep .el-table td .count-link-fail {
  color: #dc2626 !important;
}

.tb-sync-log-panel ::v-deep .el-table td .count-link-skip {
  color: #d97706 !important;
}

.mini-stat-clickable {
  cursor: pointer !important;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.mini-stat-clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.mini-stat-clickable .mini-stat-value {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.dialog-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 48px 16px;
  color: #64748b;
  font-size: 14px;
}

.detail-summary-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 12px;
}

.summary-status {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.summary-status-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.summary-dept {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
}

.summary-stats {
  display: flex;
  gap: 10px;
}

.mini-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 56px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.mini-stat-label {
  font-size: 11px;
  font-weight: 600;
}

.mini-stat-value {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.stat-ok {
  background: #f0fdf4;
  color: #166534;
}

.stat-fail {
  background: #fef2f2;
  color: #b91c1c;
}

.stat-skip {
  background: #fff7ed;
  color: #c2410c;
}

.detail-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 0;
  line-height: 1.4;
}

.detail-meta-row i {
  margin-right: 4px;
  color: #94a3b8;
}

.detail-error-msg {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 13px;
  color: #b45309;
}

.skip-hint {
  color: #94a3b8;
  font-size: 12px;
}

.detail-toolbar {
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.detail-toolbar-left {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.detail-search-input {
  width: 220px;
}

.detail-skip-legacy-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 13px;
  color: #b45309;
  line-height: 1.5;
}

.detail-skip-legacy-hint i {
  margin-top: 2px;
  flex-shrink: 0;
}

.dialog-footer {
  text-align: right;
}

.detail-id-cell {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
}

.detail-id-cell-ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-remark-cell {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.4;
}

::v-deep .custom-table.el-table th {
  background: #f8fafc !important;
  color: #475569 !important;
  font-weight: 600 !important;
}

::v-deep .custom-table.el-table {
  border-radius: 8px;
  overflow: hidden;
}
</style>

<style>
.tb-sync-detail-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.tb-sync-detail-dialog .el-dialog__header {
  padding: 16px 20px 12px;
  border-bottom: 1px solid #e2e8f0;
}

.tb-sync-detail-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.tb-sync-detail-dialog .el-dialog__body {
  padding: 16px 20px 8px;
  max-height: 72vh;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 #f1f5f9;
}

.tb-sync-detail-dialog .el-dialog__body::-webkit-scrollbar {
  width: 6px;
}

.tb-sync-detail-dialog .el-dialog__body::-webkit-scrollbar-thumb {
  background: #94a3b8;
  border-radius: 4px;
}

.tb-sync-detail-dialog .el-dialog__footer {
  padding: 10px 20px 14px;
  border-top: 1px solid #f1f5f9;
}

.tb-sync-detail-dialog .detail-body-paging {
  margin-top: 12px;
  text-align: right;
  overflow: hidden;
}

.tb-sync-detail-dialog .detail-body-paging .el-pagination {
  display: inline-flex !important;
  width: auto !important;
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  float: none;
}

.tb-sync-detail-dialog .detail-body-paging .el-pagination .btn-prev,
.tb-sync-detail-dialog .detail-body-paging .el-pagination .btn-next,
.tb-sync-detail-dialog .detail-body-paging .el-pagination .el-pager li {
  min-width: 28px;
  height: 28px;
  line-height: 28px;
  font-size: 13px;
}

.tb-sync-detail-dialog .detail-body-paging .el-pagination .el-pagination__total {
  font-size: 13px;
  margin-right: 10px;
}

.tb-sync-detail-dialog .dialog-table .el-table__body-wrapper,
.tb-sync-detail-dialog .dialog-table .el-table__header-wrapper {
  overflow-x: hidden;
}

.tb-sync-log-panel .pagination-bar .el-pagination,
.tb-sync-detail-dialog .pagination-bar .el-pagination {
  display: inline-flex !important;
  width: auto !important;
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  float: right;
}

.tb-sync-log-panel .pagination-bar .el-pagination .btn-prev,
.tb-sync-log-panel .pagination-bar .el-pagination .btn-next,
.tb-sync-log-panel .pagination-bar .el-pagination .el-pager li,
.tb-sync-detail-dialog .pagination-bar .el-pagination .btn-prev,
.tb-sync-detail-dialog .pagination-bar .el-pagination .btn-next,
.tb-sync-detail-dialog .pagination-bar .el-pagination .el-pager li {
  min-width: 28px;
  height: 28px;
  line-height: 28px;
  font-size: 13px;
}

.tb-sync-log-panel .pagination-bar .el-pagination .el-pagination__total,
.tb-sync-detail-dialog .pagination-bar .el-pagination .el-pagination__total {
  font-size: 13px;
  margin-right: 10px;
}

.tb-sync-detail-dialog .pagination-bar {
  margin-top: 12px;
  text-align: right;
  overflow: hidden;
}

.tb-sync-detail-dialog .detail-id-cell {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
}

.tb-sync-detail-dialog .detail-id-cell-ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tb-sync-detail-dialog .detail-remark-cell {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.4;
}
</style>
